import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import pillarsLogo from "../assets/logo/pillars.svg";
import { authenticate } from "../api/queries";
import { setToken } from "../auth/token";
import { PButton } from "../ui/PButton";
import { PrimeGlyph } from "../ui/PrimeGlyph";
import { useToast } from "../ui/toast";

// PORT OF `src/app/_pages/login`, CLASS FOR CLASS: A 56PX-RADIUS FRAME WHOSE 0.3REM EDGE IS A
// PRIMARY-TO-CLEAR GRADIENT AROUND A 53PX-RADIUS CARD (`py-8 px-5 sm:px-8`); THE 75PX LOGO AND
// "PILLARS" (`text-3xl font-medium`); `text-xl font-medium` LABELS OVER 1REM-PADDED FIELDS
// (THE PASSWORD WITH ITS SHOW / HIDE EYE); A FULL-WIDTH `p-3 text-xl` SIGN IN. PRIMENG'S
// PASSWORD-STRENGTH POPUP (`p-password` DEFAULTS `feedback` ON) IS NOT REPRODUCED.
export function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  // THE ANGULAR LOGIN WAITS A FIXED 1000MS BEFORE POSTING AND 100MS BEFORE
  // NAVIGATING. NEITHER IS NEEDED HERE: THE MUTATION'S PENDING STATE DRIVES THE
  // BUTTON, AND ROLES/HOSTS LOAD AS QUERIES WHEN THE FIRST PAGE ASKS FOR THEM.
  const login = useMutation({
    mutationFn: async () => {
      const resp = await authenticate(username, password);
      // A 200 WITHOUT A TOKEN IS A FAILED SIGN-IN, SAME AS THE ANGULAR CHECK
      if (!resp.data?.token) throw new Error("No token returned");
      return resp.data.token;
    },
    onSuccess: (token) => {
      // A FRESH SIGN-IN NEVER INHERITS ANOTHER USER'S CACHE
      queryClient.clear();
      setToken(token);
      void navigate({ to: "/" });
    },
    // ANGULAR'S TOAST, VERBATIM
    onError: () => toast("error", "Login", "Account not found, please check your username and password and try again."),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (username && password) login.mutate();
  };

  return (
    <div className="surface-ground flex align-items-center justify-content-center min-h-screen min-w-screen overflow-hidden">
      <div className="flex flex-column align-items-center justify-content-center">
        <div style={{ borderRadius: 56, padding: "0.3rem", background: "linear-gradient(180deg, var(--primary-color) 10%, rgba(33, 150, 243, 0) 30%)" }}>
          <form className="w-full surface-card py-8 px-5 sm:px-8" style={{ borderRadius: 53 }} onSubmit={submit}>
            <div className="text-center mb-5">
              <img src={pillarsLogo} alt="logo" height={75} className="mb-3" />
              <div className="text-900 text-3xl font-medium mb-3">pillars</div>
            </div>

            <div>
              <label htmlFor="login-username" className="block text-900 text-xl font-medium mb-2">
                Username
              </label>
              <input
                id="login-username"
                type="text"
                placeholder="Username"
                autoComplete="username"
                className="p-inputtext w-full mb-5"
                style={{ padding: "1rem" }}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
              />

              <label htmlFor="login-password" className="block text-900 font-medium text-xl mb-2">
                Password
              </label>
              {/* `p-password` WITH `toggleMask`: AN INLINE-FLEX WRAPPER AROUND A BLOCK INPUT - WHICH IS
                  WHAT SIZES THE CARD, SINCE THE BLOCK INPUT'S 20-CHARACTER WIDTH IS ITS MINIMUM -
                  WITH THE EYE 0.75REM IN FROM THE RIGHT, CENTERED */}
              <div className="w-full mb-5" style={{ position: "relative", display: "inline-flex" }}>
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  autoComplete="current-password"
                  className="p-inputtext w-full p-3"
                  style={{ display: "block" }}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((s) => !s)}
                  style={{
                    position: "absolute",
                    right: "0.75rem",
                    top: "50%",
                    marginTop: "-0.5rem",
                    padding: 0,
                    border: 0,
                    background: "none",
                    color: "#495057",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  <PrimeGlyph name={showPassword ? "eye-slash" : "eye"} />
                </button>
              </div>

              <PButton type="submit" label="Sign In" className="w-full p-3 text-xl" disabled={!username || !password} loading={login.isPending} />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
